import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { Plus, Trash, FloppyDisk, PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatEUR, parseAmount } from "../lib/format";
import { ExpenseDuplicatePanel } from "../components/ExpenseDuplicatePanel";

const MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function currentYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function ymLabel(ym) {
  if (!ym || ym.length < 7) return "—";
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTHS[idx] || m} ${y}`;
}

const DEFAULT_CATEGORIES = [
  "Miete & Nebenkosten",
  "Personal & Gehälter",
  "Versicherungen",
  "Software & Lizenzen",
  "Marketing",
  "Bürobedarf",
  "Fahrzeuge & Reisen",
  "Steuern & Abgaben",
  "Sonstiges",
];

const effMonth = (x) => (x.recurring === false && x.paid && x.paid_date ? x.paid_date.slice(0, 7) : x.month || "");
const activeIn = (x, ym) => (x.recurring === false
  ? effMonth(x) === ym
  : (x.month || "") <= ym && (!x.end_month || x.end_month >= ym));

const emptyForm = () => ({
  name: "",
  category: "",
  amount: "",
  recurring: true,
  month: currentYM(),
  end_month: "",
  notes: "",
});

export default function ExpensesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [dups, setDups] = useState(null);

  // Filter state
  const [fMonth, setFMonth] = useState("");          // "" = alle, sonst YYYY-MM
  const [fCategory, setFCategory] = useState("");    // "" = alle
  const [fType, setFType] = useState("");            // "", "recurring", "onetime"
  const [fSearch, setFSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: d }] = await Promise.all([api.get("/expenses"), api.get("/expenses/duplicates")]);
      setItems(data);
      setDups(d);
    } catch (e) {
      toast.error("Konnte Ausgaben nicht laden");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(emptyForm());
    setEditId(null);
    setShowForm(true);
  };
  const openEdit = (it) => {
    setForm({
      name: it.name || "",
      category: it.category || "",
      amount: it.amount ?? "",
      recurring: it.recurring !== false,
      month: it.month || currentYM(),
      end_month: it.end_month || "",
      notes: it.notes || "",
      paid: !!it.paid,
      paid_date: it.paid_date || null,
    });
    setEditId(it.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error("Bitte einen Namen eingeben");
      return;
    }
    const amt = parseAmount(form.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Bitte einen gültigen Betrag eingeben");
      return;
    }
    if (form.recurring && form.end_month && form.end_month < form.month) {
      toast.error("Endmonat darf nicht vor dem Startmonat liegen");
      return;
    }
    const payload = {
      name: form.name.trim(),
      category: form.category || "",
      amount: amt,
      recurring: !!form.recurring,
      month: form.month || currentYM(),
      end_month: form.recurring && form.end_month ? form.end_month : null,
      notes: form.notes || "",
      paid: !!form.paid,
      paid_date: form.paid_date || null,
    };
    try {
      if (editId) {
        await api.put(`/expenses/${editId}`, payload);
        toast.success("Ausgabe aktualisiert");
      } else {
        await api.post("/expenses", payload);
        toast.success("Ausgabe gespeichert");
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`„${it.name}" wirklich löschen?`)) return;
    await api.delete(`/expenses/${it.id}`);
    toast.success("Gelöscht");
    load();
  };

  const togglePaid = async (it) => {
    if (it.paid) {
      // Rücksetzen ohne Rückfrage – Toggle
      await api.patch(`/expenses/${it.id}/paid`, { paid: false });
      toast.success("Als nicht erledigt markiert");
      load();
      return;
    }
    // Als erledigt markieren: Datum abfragen (Default = heute)
    const today = new Date().toISOString().slice(0, 10);
    const input = window.prompt(`Wann wurde „${it.name}" erledigt? (JJJJ-MM-TT)`, today);
    if (input === null) return; // Abbrechen
    const pd = (input || "").trim() || today;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(pd)) {
      toast.error("Bitte Datum im Format JJJJ-MM-TT eingeben");
      return;
    }
    try {
      await api.patch(`/expenses/${it.id}/paid`, { paid: true, paid_date: pd });
      toast.success("Als erledigt markiert");
      load();
    } catch {
      toast.error("Konnte nicht als erledigt markiert werden");
    }
  };

  const { recurring, oneTime, totalRecurringActive, totalRecurringAll, totalOneTimeThisMonth, allCategories, filtered } = useMemo(() => {
    const ym = currentYM();
    // Verfügbare Kategorien (aus Daten + Defaults)
    const cats = new Set(DEFAULT_CATEGORIES);
    items.forEach((x) => { if (x.category) cats.add(x.category); });

    // Filterlogik
    const matchesFilters = (it) => {
      if (fType === "recurring" && it.recurring === false) return false;
      if (fType === "onetime" && it.recurring !== false) return false;
      if (fCategory && it.category !== fCategory) return false;
      if (fSearch) {
        const q = fSearch.toLowerCase();
        const hit = (it.name || "").toLowerCase().includes(q)
          || (it.notes || "").toLowerCase().includes(q)
          || (it.category || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      // Wiederkehrend: aktiv im Filtermonat (Start ≤ Monat ≤ Ende); Einmalig: Buchungsmonat == Filtermonat
      if (fMonth && !activeIn(it, fMonth)) return false;
      return true;
    };
    const filteredItems = items.filter(matchesFilters);

    const r = filteredItems.filter((x) => x.recurring !== false);
    const o = filteredItems.filter((x) => x.recurring === false);
    return {
      recurring: r,
      oneTime: o,
      filtered: filteredItems,
      allCategories: Array.from(cats).sort(),
      totalRecurringAll: r.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0),
      // Nur Posten, deren Start ≤ aktueller Monat — passt zur Dashboard-Berechnung
      totalRecurringActive: r
        .filter((x) => activeIn(x, fMonth || ym))
        .reduce((s, x) => s + (parseFloat(x.amount) || 0), 0),
      totalOneTimeThisMonth: o
        .filter((x) => effMonth(x) === (fMonth || ym))
        .reduce((s, x) => s + (parseFloat(x.amount) || 0), 0),
    };
  }, [items, fType, fCategory, fSearch, fMonth]);

  const hasFilter = !!(fMonth || fCategory || fType || fSearch);
  const resetFilters = () => { setFMonth(""); setFCategory(""); setFType(""); setFSearch(""); };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Fixausgaben</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1.5">
            Ausgaben verwalten
          </h1>
          <p className="text-sm text-[#5B6371] mt-1 max-w-2xl">
            Erfasse wiederkehrende Kosten (z.B. Miete, Software) und einmalige Ausgaben. Sie fließen automatisch in den Dashboard-Vergleich Einnahmen vs. Ausgaben ein.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="expense-add"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2"
        >
          <Plus size={16} /> Ausgabe hinzufügen
        </button>
      </header>

      {/* Filter Bar */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 md:p-4" data-testid="expenses-filterbar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Suche</label>
            <input className="field mt-1 !py-2" value={fSearch}
              onChange={(e) => setFSearch(e.target.value)}
              placeholder="Name, Kategorie, Notiz…"
              data-testid="expenses-filter-search" />
          </div>
          <div className="min-w-[150px]">
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Monat</label>
            <input type="month" className="field mt-1 !py-2" value={fMonth}
              onChange={(e) => setFMonth(e.target.value)}
              data-testid="expenses-filter-month" />
          </div>
          <div className="min-w-[170px]">
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Kategorie</label>
            <select className="field mt-1 !py-2" value={fCategory}
              onChange={(e) => setFCategory(e.target.value)}
              data-testid="expenses-filter-category">
              <option value="">Alle Kategorien</option>
              {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="text-[10px] uppercase tracking-widest text-[#5B6371]">Art</label>
            <select className="field mt-1 !py-2" value={fType}
              onChange={(e) => setFType(e.target.value)}
              data-testid="expenses-filter-type">
              <option value="">Alle</option>
              <option value="recurring">🔁 Wiederkehrend</option>
              <option value="onetime">⚡ Einmalig</option>
            </select>
          </div>
          {hasFilter && (
            <button onClick={resetFilters}
              data-testid="expenses-filter-reset"
              className="inline-flex items-center gap-1.5 text-sm text-[#5B6371] hover:text-[#FF6B5C] border border-[#E5E7EB] rounded-md px-3 py-2 hover:border-[#FF6B5C] transition-colors">
              <X size={14} /> Filter zurücksetzen
            </button>
          )}
        </div>
        {hasFilter && (
          <div className="mt-2.5 text-xs text-[#5B6371]" data-testid="expenses-filter-summary">
            <strong>{filtered.length}</strong> von <strong>{items.length}</strong> Posten gefiltert
            {fMonth && <> · Monat: <strong>{ymLabel(fMonth)}</strong></>}
            {fCategory && <> · Kategorie: <strong>{fCategory}</strong></>}
            {fType === "recurring" && <> · nur wiederkehrend</>}
            {fType === "onetime" && <> · nur einmalig</>}
          </div>
        )}
      </div>

      <ExpenseDuplicatePanel data={dups} onDelete={onDelete} />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="kpi-recurring-total">
          <div className="text-xs uppercase tracking-widest text-[#5B6371]">Wiederkehrend / Monat</div>
          <div className="font-display text-3xl font-bold text-[#FF6B5C] mt-2">{formatEUR(totalRecurringActive)}</div>
          <div className="text-xs text-[#94A3B8] mt-1">
            {recurring.length} Posten
            {totalRecurringAll > totalRecurringActive && (
              <span> · {formatEUR(totalRecurringAll - totalRecurringActive)} nicht aktiv (später/beendet)</span>
            )}
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="kpi-onetime-month">
          <div className="text-xs uppercase tracking-widest text-[#5B6371]">Einmalig dieser Monat</div>
          <div className="font-display text-3xl font-bold text-[#1F242E] mt-2">{formatEUR(totalOneTimeThisMonth)}</div>
          <div className="text-xs text-[#94A3B8] mt-1">{ymLabel(fMonth || currentYM())} · nach Zahlungsmonat</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="kpi-total-month">
          <div className="text-xs uppercase tracking-widest text-[#5B6371]">Gesamt dieser Monat</div>
          <div className="font-display text-3xl font-bold text-[#FF6B5C] mt-2">{formatEUR(totalRecurringActive + totalOneTimeThisMonth)}</div>
          <div className="text-xs text-[#94A3B8] mt-1">Wiederkehrend + einmalig</div>
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg w-full max-w-lg p-6 mt-12" onClick={(e) => e.stopPropagation()} data-testid="expense-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">{editId ? "Ausgabe bearbeiten" : "Neue Ausgabe"}</h2>
              <button onClick={() => setShowForm(false)} className="text-[#5B6371] hover:text-[#1F242E]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">Name *</label>
                <input className="field mt-1" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Miete Büro Berlin"
                  data-testid="expense-form-name" autoFocus />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">Betrag €*</label>
                  <input type="text" inputMode="decimal" className="field mt-1" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="1500.00"
                    data-testid="expense-form-amount" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">Kategorie</label>
                  <input list="expense-cat-list" className="field mt-1" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="Wähle oder gib ein…"
                    data-testid="expense-form-category" />
                  <datalist id="expense-cat-list">
                    {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371] mb-2 block">Art</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setForm({ ...form, recurring: true })}
                    data-testid="expense-form-recurring-yes"
                    className={`px-3 py-2.5 text-sm rounded-md border ${form.recurring ? "bg-[#0AA09E] text-white border-[#0AA09E]" : "bg-white border-[#E5E7EB] text-[#1F242E] hover:bg-[#F8FAFC]"}`}>
                    🔁 Wiederkehrend (jeden Monat)
                  </button>
                  <button type="button"
                    onClick={() => setForm({ ...form, recurring: false })}
                    data-testid="expense-form-recurring-no"
                    className={`px-3 py-2.5 text-sm rounded-md border ${!form.recurring ? "bg-[#0AA09E] text-white border-[#0AA09E]" : "bg-white border-[#E5E7EB] text-[#1F242E] hover:bg-[#F8FAFC]"}`}>
                    ⚡ Einmalig
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">
                  {form.recurring ? "Startmonat" : "Monat"}
                </label>
                <input type="month" className="field mt-1" value={form.month}
                  onChange={(e) => setForm({ ...form, month: e.target.value })}
                  data-testid="expense-form-month" />
                <p className="text-xs text-[#94A3B8] mt-1">
                  {form.recurring ? "Ab diesem Monat fällt die Ausgabe in jedem Monat an." : "Ausgabe wird nur in diesem Monat berücksichtigt."}
                </p>
              </div>

              {form.recurring && (
                <div>
                  <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">Endmonat (optional)</label>
                  <div className="flex gap-2 mt-1">
                    <input type="month" className="field" value={form.end_month} min={form.month}
                      onChange={(e) => setForm({ ...form, end_month: e.target.value })}
                      data-testid="expense-form-end-month" />
                    {form.end_month && (
                      <button type="button" onClick={() => setForm({ ...form, end_month: "" })}
                        className="px-3 text-xs border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]"
                        data-testid="expense-form-end-month-clear">Kein Ende</button>
                    )}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-1">Leer = läuft unbegrenzt weiter. Mit Ende zählt die Ausgabe nur bis einschließlich diesem Monat.</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium uppercase tracking-widest text-[#5B6371]">Notiz (optional)</label>
                <input className="field mt-1" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="expense-form-notes" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]"
                  data-testid="expense-form-cancel">Abbrechen</button>
                <button type="submit"
                  className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2"
                  data-testid="expense-form-submit">
                  <FloppyDisk size={16} /> {editId ? "Aktualisieren" : "Speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Wiederkehrend */}
      <section>
        <h2 className="font-display text-lg font-semibold mb-3">🔁 Wiederkehrende Ausgaben</h2>
        <ExpenseTable
          items={recurring}
          empty="Keine wiederkehrenden Ausgaben angelegt."
          loading={loading}
          showMonthAsStart
          flagged={dups?.flagged_ids}
          onEdit={openEdit}
          onDelete={onDelete}
        />
      </section>

      {/* Einmalig */}
      <section>
        <h2 className="font-display text-lg font-semibold mb-3">⚡ Einmalige Ausgaben</h2>
        <ExpenseTable
          items={oneTime}
          empty="Keine einmaligen Ausgaben angelegt."
          loading={loading}
          showMonthAsStart={false}
          showPaid
          flagged={dups?.flagged_ids}
          onEdit={openEdit}
          onDelete={onDelete}
          onTogglePaid={togglePaid}
        />
      </section>
    </div>
  );
}

function ExpenseTable({ items, empty, loading, showMonthAsStart, showPaid, flagged = [], onEdit, onDelete, onTogglePaid }) {
  const total = items.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Kategorie</th>
              <th className="text-left px-4 py-3 font-medium">{showMonthAsStart ? "Laufzeit" : "Monat"}</th>
              <th className="text-right px-4 py-3 font-medium">Betrag</th>
              {showPaid && <th className="text-center px-4 py-3 font-medium">Erledigt</th>}
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={showPaid ? 6 : 5} className="px-4 py-8 text-center text-sm text-[#94A3B8]">Lade …</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={showPaid ? 6 : 5} className="px-4 py-10 text-center text-sm text-[#94A3B8]">{empty}</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] ${showPaid && it.paid ? "opacity-60" : ""}`} data-testid={`expense-row-${it.id}`}>
                <td className="px-4 py-3 font-medium text-[#1F242E]">
                  <span className={showPaid && it.paid ? "line-through decoration-[#94A3B8]" : ""}>{it.name}</span>
                  {flagged.includes(it.id) && (
                    <span className="ml-2 inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF3DC] text-[#B45309]" data-testid={`expense-dup-badge-${it.id}`}>
                      Mögliche Doppelung
                    </span>
                  )}
                  {it.notes && <div className="text-xs text-[#94A3B8] mt-0.5">{it.notes}</div>}
                </td>
                <td className="px-4 py-3 text-[#5B6371]">{it.category || "—"}</td>
                <td className="px-4 py-3 text-[#5B6371]" data-testid={`expense-period-${it.id}`}>
                  {showMonthAsStart
                    ? <>ab {ymLabel(it.month)}{it.end_month ? <> – bis {ymLabel(it.end_month)}</> : <span className="text-[#94A3B8]"> · ohne Ende</span>}</>
                    : ymLabel(it.month)}
                </td>
                <td className="px-4 py-3 text-right font-display font-semibold text-[#FF6B5C]">{formatEUR(it.amount || 0)}</td>
                {showPaid && (
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => onTogglePaid?.(it)}
                      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        it.paid
                          ? "bg-[#E6F7F7] text-[#0AA09E] hover:bg-[#D1EFEE]"
                          : "bg-[#F8FAFC] text-[#5B6371] border border-[#E5E7EB] hover:bg-[#F1F5F9]"
                      }`}
                      title={it.paid ? `Erledigt am ${it.paid_date} — klicken zum Rücksetzen` : "Als erledigt markieren"}
                      data-testid={`expense-toggle-paid-${it.id}`}
                    >
                      <span className={`inline-block w-3.5 h-3.5 rounded-sm border ${it.paid ? "bg-[#0AA09E] border-[#0AA09E]" : "border-[#94A3B8]"}`}>
                        {it.paid && <span className="text-white text-[10px] leading-none block -mt-0.5">✓</span>}
                      </span>
                      {it.paid ? (it.paid_date || "erledigt") : "offen"}
                    </button>
                  </td>
                )}
                <td className="px-2 py-3 whitespace-nowrap text-right">
                  <button onClick={() => onEdit(it)} className="text-[#5B6371] hover:text-[#0AA09E] p-1.5" title="Bearbeiten" data-testid={`expense-edit-${it.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => onDelete(it)} className="text-[#5B6371] hover:text-[#FF6B5C] p-1.5" title="Löschen" data-testid={`expense-delete-${it.id}`}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="bg-[#F8FAFC] border-t-2 border-[#0AA09E] font-display font-bold">
                <td className="px-4 py-3" colSpan={3}>Summe</td>
                <td className="px-4 py-3 text-right text-[#FF6B5C]">{formatEUR(total)}</td>
                {showPaid && <td></td>}
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
